import * as stylex from "@stylexjs/stylex";
import React from "react";
import { useRTL } from "../../hooks";
import { colors } from "../../materials/consts.stylex";
import { typography } from "../../materials/typography.stylex";
import type { MarkStyle } from "./types";

const styles = stylex.create({
  container: {
    marginBottom: "10px",
  },
  directionLtr: {
    direction: "ltr",
  },
  directionRtl: {
    direction: "rtl",
  },
  inlineContainer: {
    marginBottom: "0px",
    lineHeight: "12px",
  },
  title: {
    color: colors.blackText,
  },
  label: {
    color: colors.lightText,
  },
  labelWithColor: {
    position: "relative",
  },
  paddingStartLtr: {
    paddingLeft: "12px",
  },
  paddingStartRtl: {
    paddingRight: "12px",
  },
  inlineLabel: {
    display: "inline-block",
  },
  marginEndLtr: {
    marginRight: "12px",
  },
  marginEndRtl: {
    marginLeft: "12px",
  },
  color: {
    position: "absolute",
    top: "4px",
    width: "8px",
    height: "8px",
  },
  colorSmall: {
    position: "absolute",
    top: "6px",
    width: "4px",
    height: "4px",
  },
  startLtr: {
    left: "0px",
  },
  startRtl: {
    right: "0px",
  },
  circle: {
    borderRadius: "50%",
  },
});

export interface ColorLegendEntry {
  color?: string; // omitted entries render label-only (see `!!color &&` below)
  label: React.ReactNode;
  style?: MarkStyle;
}

export interface ColorLegendProps {
  values: Array<ColorLegendEntry>;
  maxWidth: number | string;
  inline?: boolean;
  title?: React.ReactNode;
}

const ColorLegend_ = ({ title, values, maxWidth, inline }: ColorLegendProps) => {
  const isRTL = useRTL();

  if (!values.length && !title) {
    return null;
  }
  return (
    <div
      {...stylex.props(
        styles.container,
        isRTL ? styles.directionRtl : styles.directionLtr,
        inline && styles.inlineContainer,
      )}
      style={{ maxWidth }}
    >
      {!!title && <div {...stylex.props(typography.table, styles.title)}>{title}</div>}
      {values.map(({ label, color, style = "normal" }, i) => {
        const text = label;

        return (
          <div
            key={i}
            {...stylex.props(
              typography.meta,
              styles.label,
              inline && styles.inlineLabel,
              inline && (isRTL ? styles.marginEndRtl : styles.marginEndLtr),
              !!color && styles.labelWithColor,
              !!color && (isRTL ? styles.paddingStartRtl : styles.paddingStartLtr),
            )}
          >
            {isRTL && text}
            {!!color && (
              <div
                {...stylex.props(
                  style === "normal" ? styles.color : styles.colorSmall,
                  styles.circle,
                  isRTL ? styles.startRtl : styles.startLtr,
                )}
                style={{ backgroundColor: color }}
              />
            )}
            {!isRTL && text}
          </div>
        );
      })}
    </div>
  );
};
const ColorLegend = React.memo(ColorLegend_);

export default ColorLegend;
